// src/helpers/pdfPendientesProveedor.js

import { jsPDF } from "jspdf";

import {
  fmtDate,
  fmtNum,
  S,
  loadImageAsDataURL,
  drawHeader,
} from "./pdfCommonSanPancracio";

/**
 * Genera un PDF con el resumen de remitos pendientes por PROVEEDOR.
 *
 * Orden de Pago:
 * - Pendiente Compra A
 * - Pendiente Compra B
 *
 * El Flete NO forma parte de la deuda del proveedor.
 */
export async function generarPDFPendientesProveedor({
  proveedor,
  cliente = null,
  pendientes = [],
  totales,
  fecha,
  logoUrl = "/img/logo-sanpancracio.png",
  empresa = {},
}) {
  if (!pendientes.length) {
    alert("No hay remitos pendientes para este proveedor.");
    return;
  }

  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
  });

  const logoDataURL = await loadImageAsDataURL(logoUrl);

  let y = 12;

  // Encabezado
  drawHeader(doc, y, logoDataURL, empresa);

  y += 34;

  // Título
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);

  doc.text(
    "RESUMEN DE REMITOS PENDIENTES PARA ORDEN DE PAGO",
    105,
    y,
    { align: "center" }
  );

  y += 8;

  // Proveedor / cliente / fecha
  const provNombre = S(
    proveedor?.razonsocial ||
      proveedor?.nombre ||
      proveedor ||
      "-"
  );

  const cliNombre = cliente
    ? S(
        cliente?.razonsocial ||
          cliente?.nombre ||
          cliente
      )
    : null;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  doc.text(
    `Proveedor: ${provNombre}`,
    12,
    y
  );

  if (cliNombre) {
    doc.text(
      `Cliente (filtro): ${cliNombre}`,
      12,
      y + 5
    );
  }

  doc.text(
    `Fecha: ${fmtDate(fecha)}`,
    198,
    y,
    { align: "right" }
  );

  y += cliNombre ? 12 : 8;

  // --------------------------------------------------
  // TABLA
  // --------------------------------------------------

  const startX = 12;

  const colRem = 28;
  const colFecha = 28;
  const colA = 48;
  const colB = 48;
  const colSaldo = 34;

  const totalWidth =
    colRem +
    colFecha +
    colA +
    colB +
    colSaldo;

  const headerY = y;

  doc.setFillColor(225, 235, 255);
  doc.setDrawColor(180, 180, 200);

  doc.rect(
    startX,
    headerY - 5,
    totalWidth,
    7,
    "F"
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);

  doc.text(
    "Remito",
    startX + 2,
    headerY
  );

  doc.text(
    "Fecha",
    startX + colRem + 2,
    headerY
  );

  doc.text(
    "Pend. Compra A",
    startX +
      colRem +
      colFecha +
      colA -
      2,
    headerY,
    { align: "right" }
  );

  doc.text(
    "Pend. Compra B",
    startX +
      colRem +
      colFecha +
      colA +
      colB -
      2,
    headerY,
    { align: "right" }
  );

  doc.text(
    "Saldo",
    startX + totalWidth - 2,
    headerY,
    { align: "right" }
  );

  y += 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  const maxY = 260;

  const filasOrdenadas = [...pendientes].sort(
    (a, b) =>
      new Date(a.fecha || 0) -
      new Date(b.fecha || 0)
  );

  for (const r of filasOrdenadas) {
    if (y > maxY) {
      doc.addPage();
      y = 20;
    }

    // Igual que en el modal de Orden de Pago:
    // sólo pendientes de Compra A + Compra B.

    const pendA =
      Number(
        r.pendienteA ??
          Math.max(
            0,
            Number(r.totalCompraA || 0) -
              Number(r.pagadoA || 0)
          )
      ) || 0;

    const pendB =
      Number(
        r.pendienteB ??
          Math.max(
            0,
            Number(r.totalCompraB || 0) -
              Number(r.pagadoB || 0)
          )
      ) || 0;

    const saldo = Number(
      (pendA + pendB).toFixed(2)
    );

    const nroRem =
      r.codrem != null
        ? String(r.codrem).padStart(6, "0")
        : "-";

    const rowY = y;

    doc.setDrawColor(230);

    doc.line(
      startX,
      rowY + 3,
      startX + totalWidth,
      rowY + 3
    );

    doc.text(
      `#${nroRem}`,
      startX + 2,
      rowY + 2
    );

    doc.text(
      fmtDate(r.fecha),
      startX + colRem + 2,
      rowY + 2
    );

    doc.text(
      `$ ${fmtNum(pendA)}`,
      startX +
        colRem +
        colFecha +
        colA -
        2,
      rowY + 2,
      { align: "right" }
    );

    doc.text(
      `$ ${fmtNum(pendB)}`,
      startX +
        colRem +
        colFecha +
        colA +
        colB -
        2,
      rowY + 2,
      { align: "right" }
    );

    doc.text(
      `$ ${fmtNum(saldo)}`,
      startX + totalWidth - 2,
      rowY + 2,
      { align: "right" }
    );

    y += 6;
  }

  // --------------------------------------------------
  // RESUMEN
  // --------------------------------------------------

  y += 4;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);

  doc.setDrawColor(120, 120, 160);
  doc.setFillColor(220, 230, 255);

  const resumenY = y;
  const resumenHeight = 12;

  doc.rect(
    startX,
    resumenY - 6,
    totalWidth,
    resumenHeight,
    "F"
  );

  // Línea 1
  doc.text(
    `Σ Compra A: $ ${fmtNum(
      totales?.compraA
    )}   —   Σ Compra B: $ ${fmtNum(
      totales?.compraB
    )}`,
    startX + 2,
    resumenY - 1
  );

  // Línea 2
  doc.text(
    `Σ Saldo total: $ ${fmtNum(
      totales?.saldo
    )}`,
    startX + 2,
    resumenY + 4
  );

  // Pie
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);

  doc.text(
    "Documento electrónico generado por el sistema San Pancracio — Resumen para Orden de Pago",
    12,
    292
  );

  const safeProv = provNombre.replace(
    /[^\w-]+/g,
    "_"
  );

  const filename =
    `Pendientes_${safeProv || "proveedor"}.pdf`;

  doc.save(filename);
}